import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { buildWhatsAppLink } from "../data/menu";
import { useCart } from "../lib/CartContext";

const navLinks = [
  { id: "sobre", label: "Sobre" },
  { id: "cardapio", label: "Cardápio" },
  { id: "galeria", label: "Galeria" },
  { id: "diferenciais", label: "Diferenciais" },
  { id: "faq", label: "FAQ" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { totalItems, setIsCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[#132117]/90 backdrop-blur-md border-b border-[var(--sn-border)]"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 group"
          data-testid="nav-logo-button"
        >
          <span className="font-display text-2xl tracking-wider text-[var(--sn-gold)] group-hover:text-[var(--sn-cream)] transition-colors">
            SANTO <span className="italic font-normal">Nhoque</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {navLinks.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="text-sm tracking-wide text-[var(--sn-cream-soft)]/85 hover:text-[var(--sn-gold)] transition-colors duration-300 relative group"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-[var(--sn-gold)] group-hover:w-full transition-all duration-500" />
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={buildWhatsAppLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[var(--sn-whatsapp)] hover:bg-[var(--sn-whatsapp-hover)] text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors"
            data-testid="nav-whatsapp-button"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor">
              <path d="M20.52 3.48A11.85 11.85 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.98L0 24l6.18-1.62A11.94 11.94 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52ZM12 22a9.93 9.93 0 0 1-5.07-1.39l-.36-.21-3.67.96.98-3.58-.23-.37A9.96 9.96 0 1 1 22 12c0 5.51-4.49 10-10 10Zm5.49-7.46c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.94 1.17-.17.2-.34.22-.64.07-.3-.15-1.27-.47-2.42-1.5a9.05 9.05 0 0 1-1.67-2.07c-.18-.3-.02-.46.13-.62.14-.13.3-.34.45-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.2-.24-.58-.49-.5-.66-.5l-.56-.01c-.2 0-.52.08-.79.37-.27.3-1.03 1-1.03 2.45s1.06 2.85 1.21 3.05c.15.2 2.08 3.17 5.04 4.45.7.3 1.25.48 1.68.62.7.22 1.34.2 1.85.12.56-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
            </svg>
            Pedir pelo WhatsApp
          </a>

          <button
            onClick={() => setIsCartOpen(true)}
            className="relative p-2 text-[var(--sn-gold)] hover:bg-[var(--sn-gold)]/10 rounded-full transition-colors"
            aria-label="Abrir carrinho"
          >
            <ShoppingBag className="w-6 h-6" />
            <AnimatePresence>
              {totalItems > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--sn-whatsapp)] text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-[#132117]"
                >
                  {totalItems}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>

        <div className="md:hidden flex items-center gap-4">
          <button
            onClick={() => setIsCartOpen(true)}
            className="relative p-2 text-[var(--sn-gold)] hover:bg-[var(--sn-gold)]/10 rounded-full transition-colors"
            aria-label="Abrir carrinho"
          >
            <ShoppingBag className="w-6 h-6" />
            <AnimatePresence>
              {totalItems > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--sn-whatsapp)] text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-[#132117]"
                >
                  {totalItems}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
          <button
            className="text-[var(--sn-gold)] p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#132117]/95 backdrop-blur-md border-t border-[var(--sn-border)]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {navLinks.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="text-left text-[var(--sn-cream)] text-lg font-display"
                  data-testid={`nav-mobile-link-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <a
                href={buildWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[var(--sn-whatsapp)] text-white font-medium px-5 py-3 rounded-md"
                data-testid="nav-mobile-whatsapp-button"
              >
                Pedir pelo WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
